<template>
  <div>
    <ProgressBar active-step="Tell us about your job" />
    <PageHeading
      :default-heading="pageHeader"
      :edit-heading="pageHeader"
    />
    <form
      novalidate="novalidate"
      @submit.prevent
    >
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          {{ areTheyEmployedHeader }}
        </h2>
        <apx-select
          id="employmentStatus"
          :label="pd_content.employment_status_label"
          :error-text="pd_content.employment_status_error"
          :value="su_employmentStatus"
          required
        />
        <div
          v-if="!isManagedAccount"
          class="pvd-form-row no-margin-top"
        >
          <div class="form-row-content">
            <div class="form-row-suplemental">
              <p class="xsmall">
                <a
                  id="help-popover-trigger"
                  href="javascript:void(0);"
                >
                  {{ pd_content.help_me_choose }}</a>
                <!-- Modal body-->
              </p>
              <ModalComponent
                id-val="help-popover"
                :modal-header="pd_content.help_me_choose_modal_header"
                :modal-body1="pd_content.help_me_choose_modal"
                modal-class="small"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        v-show="showEmployerDetailsSection"
        class="pvd-container container-divider"
      >
        <h2 class="pvd-heading-L">
          {{ pd_content.employment_details.section_heading_label }}
        </h2>
        <apx-text-input
          id="employerOccupation"
          v-observe-visibility="occupationVisible"
          :value="su_occupation"
          id-prefix="employerOccupation"
          :label="`${su_firstName}'s ${pd_content.employment_details.youth_occupation_label}`"
          :track-events="true"
        />
        <apx-text-input
          id="companyName"
          v-observe-visibility="companyNameVisible"
          :value="su_employersName"
          id-prefix="companyName"
          :label="pd_content.employment_details.company_name_label"
          :track-events="true"
        />
        <apx-toggle
          v-show="su_employmentStatus === 'S'"
          id="toggleEmployerAddrSame"
          :yes-label="pd_content.employment_details.yes_toggle_label"
          :no-label="pd_content.employment_details.no_toggle_label"
          :value="su_employerAddressSameAsLegal"
          :track-events="true"
          :group-label="`${pd_content.employment_details.is} ${su_firstName} ${pd_content.employment_details.youth_addr_same_label}`"
        />
        <div
          v-show="su_employerAddressSameAsLegal === 'Yes' && su_employmentStatus === 'S'"
          class="pvd-display-row"
        >
          <div class="display-row-label">
            <p>{{ pd_content.employment_details.company_address_label }}</p>
          </div>
          <div class="display-row-content">
            <p>{{ su_leg_line1 }} {{ su_leg_line2 }}</p>
            <p>{{ su_leg_city }}, {{ su_leg_state }} {{ su_leg_zip }}</p>
          </div>
        </div>
        <apx-address
          v-show="showAddressSection"
          id="address"
          :address-line1="su_emp_line1"
          :address-line2="su_emp_line2"
          :city-name="su_emp_city"
          :state-code="su_emp_state"
          :zip-code="su_emp_zip"
          :address-labels="JSON.stringify(addressLabels)"
          required
        />
      </div>
      <div
        v-show="showAssociations"
        id="associations"
        class="pvd-container container-divider"
        style="display: block;"
      >
        <h2 class="pvd-heading-L">
          {{ pd_content.associations_section_header }}
        </h2>
        <div
          v-if="showCompanyNameSection"
          style="display: block;"
        >
          <apx-toggle
            v-if="showAssociationToggle"
            id="toggleAssociation"
            v-observe-visibility="associationToggleBecameVisible"
            stack
            :yes-label="pd_content.yes_toggle_text"
            :no-label="pd_content.no_toggle_text"
            :value="su_coAffillCode"
            :track-events="true"
            :required-message="pd_content.toggle_text_association"
            :group-label="`${pd_content.association_information_section_header.is}
                          ${su_firstName + ','}
                          ${pd_content.association_information_section_header.or_text}`"
            :help-header="pd_content.associations_modal_header"
            :help-body="pd_content.associations_modal_body"
            help-size="small"
          />
          <div
            v-if="showCompanyName"
            id="association1"
            style="display: block;"
          >
            <apx-autocomplete
              id="companyNameInput"
              v-observe-visibility="companyNameBecameVisible"
              :autocomplete-label="pd_content.association_label"
              :selected-value="su_coAffillName"
            />
            <div class="pvd-form-row nomargin">
              <div class="form-row-supplemental">
                <p class="xsmall">
                  {{ pd_content.association_supplemental_text }}
                </p>
              </div>
            </div>
          </div>
          <div class="pvd-form-row">
            <div class="form-row-label-group toggle-button-stacked">
              <label>
                {{ `${pd_content.control_person_question.question1} ${su_firstName} ${pd_content.control_person_question.question2}` }}
                <a
                  id="tt-popover-trigger"
                  href="javascript:void(0);"
                  @click="track('any_of_your_relatives', 'interaction')"
                >{{ `${pd_content.control_person_question.question_link}` }}
                </a> {{ pd_content.control_person_question.question3 }}</label>
            </div>
            <ToggleComponent
              :id-prefix="'controlPersonToggle'"
              :toggle-value="su_coDirectorCode"
              :yes-label="pd_content.yes_toggle_text"
              :no-label="pd_content.no_toggle_text"
              :track-events="true"
              :validation-rules="pd_content.control_person_validation_rules"
              :message="pd_content.toggle_text_association"
              :conditional-class="'form-row-content'"
              @update:toggleValue="controlPersonToggleChanged"
            />
          </div>
          <ModalComponent
            id-val="tt-popover"
            :modal-body1="pd_content.control_person_modal_body"
            modal-class="small"
          />
          <apx-autocomplete
            v-if="showControlPersonInput"
            id="controlPersonInput"
            v-observe-visibility="cpBecameVisible"
            :selected-value="su_coDirectorSymbol"
            :autocomplete-label="pd_content.trading_or_cusip_label"
            start-auto-suggest="1"
          />
        </div>
      </div>
      <PageNavigation
        show-primary-button-spinner="N"
        enable-exit-modal="Y"
        show-secondary-button="Y"
        show-page-spinner="N"
        primary-button-type="button"
        :primary-button-label="getPrimaryButtonLabel"
        :secondary-button-label="getSecondaryButtonLabel"
        :page-exit-url="computedExitUrl"
        @primary-button-clicked="nextButtonClicked"
      />
    </form>
  </div>
</template>

<script>
import Vue from 'vue';
import VueObserveVisibility from 'vue-observe-visibility';
import ToggleComponent from '../components/ToggleComponent.vue';
import PageNavigation from '../components/PageNavigation.vue';
import ModalComponent from '../components/ModalComponent.vue';
import PageHeading from '../components/PageHeading.vue';
import utility from '../mixins/utility';
import ProgressBar from '../components/ProgressBar.vue';
import dataHelper from '../mixins/dataHelper';
import pageNavigationHelper from '../mixins/pageNavigationHelper';
import tradingSymbolAutosuggest from '../mixins/tradingSymbolAutosuggest';
import dataSanitize from '../mixins/dataSanitize';
import validationHelper from '../mixins/validationHelper';
import addressAutocomplete from '../mixins/addressAutocomplete';
import common from '../mixins/common';
import youthHelper from '../mixins/youthHelper';

Vue.use(VueObserveVisibility);
const changeEventName = 'value-changed';

export default {
  name: 'JointEmploymentInformation',
  components: {
    ProgressBar,
    PageHeading,
    PageNavigation,
    ToggleComponent,
    ModalComponent,
  },
  mixins: [dataHelper, pageNavigationHelper, utility, tradingSymbolAutosuggest, dataHelper, dataSanitize,
    validationHelper, addressAutocomplete, youthHelper],
  data() {
    return {
      backButtonAttempt: false,
      mapObject: {},
    };
  },
  computed: {
    isManagedAccount() {
      return this.isManaged(this.routeId);
    },
    pageHeader() {
      const pageHeader = this.pd_content.page_header;
      if (pageHeader) {
        let heading;
        if (this.isManagedAccount) {
          heading = `${this.pd_content.page_header.tell_us} ${this.su_firstName} ${this.pd_content.page_header.has_a_job}`;
        } else {
          heading = this.cs_editMode ? this.replaceName(pageHeader.edit) : this.replaceName(this.pd_content.page_header.default);
        }
        if (heading) {
          document.title = this.setTitle(heading);
        }
        return heading;
      }
      return '';
    },
    showAssociations() {
      return this.su_employmentStatus !== '';
    },
    showAssociationToggle() {
      return this.su_employmentStatus !== 'Z';
    },
    showEmployerDetailsSection() {
      const employerDetailsSection = ['E', 'S'];
      if (this.isManagedAccount) {
        employerDetailsSection.push('Z');
      }
      return employerDetailsSection.includes(this.su_employmentStatus);
    },
    showCompanyNameSection() {
      return this.showAssociations;
    },
    showCompanyName() {
      return this.su_coAffillCode === 'Yes';
    },
    showAddressSection() {
      const employerDetailsSection = ['E'];
      if (this.isManagedAccount) {
        employerDetailsSection.push('Z');
      }
      const visible = (this.su_employerAddressSameAsLegal === 'No' && this.su_employmentStatus === 'S')
       || employerDetailsSection.includes(this.su_employmentStatus);

      if (visible) {
        this.configureAddressSection(this.buildRules('address'));
      } else {
        this.configureAddressSection(common.optionalAddress);
      }
      return visible;
    },
    showControlPersonInput() {
      return this.su_coDirectorCode === 'Yes';
    },
    addressLabels() {
      return {
        addressLine1: this.pd_content.employment_details.address_line1_label,
        addressLine2: this.pd_content.employment_details.address_line2_label,
        cityName: this.pd_content.employment_details.city_label,
        stateCode: this.pd_content.employment_details.state_label,
        zipCode: this.pd_content.employment_details.zip_code_label,
      };
    },
    areTheyEmployedHeader() {
      const areTheyEmployed = this.pd_content.are_they_employed_header;
      if (areTheyEmployed) {
        return `${this.pd_content.are_they_employed_header.is} ${this.su_firstName} ${this.pd_content.are_they_employed_header.employed}`;
      }
      return '';
    },
  },
  created() {
    if (this.cs_editMode) {
      this.unsubscribe = this.sanitizeInstanceData('secondaryUser');
    }
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
  mounted() {
    this.trackEdit(this.cs_editMode, 'edit-joint-employment-information');
    // default status to Not employed, but still need to have Select a Status available.
    if (this.su_employmentStatus === '' && !this.isManagedAccount) {
      this.commitSecondaryUserData('N', 'employmentStatus');
    }
    this.removeModalClassIfExists();
    this.populateEmploymentStatuses('#employmentStatus');
    document.querySelector('#employmentStatus').addEventListener(changeEventName, this.employmentStatusChanged);
    document.querySelector('#employerOccupation').addEventListener(changeEventName, (event) => this.employerDetailsChanged(event, 'occupation'));
    document.querySelector('#companyName').addEventListener(changeEventName, (event) => this.employerDetailsChanged(event, 'employersName'));
    this.configureEmployerAddrSameToggle();
    this.initGoogleAutocomplete('address-addressLine1', this.fillAddressValues);
  },
  methods: {
    nextButtonClicked(navAction) {
      if (!this.showAddressSection) {
        this.addressChanged({
          detail: {
            address: {
              addressLine1: '', addressLine2: '', cityName: '', stateCode: '', zipCode: '',
            },
          },
        });
      }
      this.performPageValidation(navAction).then((valid) => {
        if (valid) {
          if (this.su_employmentStatus !== 'S') {
            this.commitSecondaryUserData('', 'employerAddressSameAsLegal');
          }
          this.performNavigation(this.pd_content.primary_button.value);
        }
      });
    },
    configureEmployerAddrSameToggle() {
      const toggleEmployerAddrSame = document.querySelector('#toggleEmployerAddrSame');
      if (toggleEmployerAddrSame) {
        if (this.pu_employmentStatus === 'N' || this.pu_employmentStatus === 'R'
          || this.su_employerAddressSameAsLegal === '' || this.su_employerAddressSameAsLegal === 'Yes') {
          this.commitSecondaryUserData('Yes', 'employerAddressSameAsLegal');
          this.configureAddressSection(common.optionalAddress);
        } else if (this.su_employerAddressSameAsLegal === 'No') {
          this.configureAddressSection(this.buildRules('address'));
        }

        toggleEmployerAddrSame
          .addEventListener(changeEventName, this.employerAddressSameToggleChanged);
        const observer = new MutationObserver(() => {
        });
        const config = {
          attributes: false,
          childList: true,
          characterData: false,
        };
        observer.observe(toggleEmployerAddrSame, config);
      }
    },
    configureAddressSection(rules) {
      const addressComponent = document.querySelector('#address');
      if (addressComponent) {
        addressComponent.removeEventListener(changeEventName, this.addressChanged);
        addressComponent.addEventListener(changeEventName, this.addressChanged);
        addressComponent.validationRules = rules;
      }
    },
    employerAddressSameToggleChanged(event) {
      const { detail } = event;
      this.commitSecondaryUserData(detail, 'employerAddressSameAsLegal');
      if (this.su_employerAddressSameAsLegal === 'Yes') {
        this.track('employerAddressSame-Yes', 'interaction');
        this.configureAddressSection(common.optionalAddress);
      } else {
        this.track('employerAddressSame-No', 'interaction');
      }
    },
    fillAddressValues(address) {
      const event = {
        detail: {
          address,
        },
      };

      const line1Element = document.getElementById('address-addressLine1');
      line1Element.value = event.detail.address.addressLine1;
      line1Element.dispatchEvent(new Event('change'));

      document.getElementById('address-addressLine2').value = event.detail.address.addressLine2;

      const cityNameElement = document.getElementById('address-cityName');
      cityNameElement.value = event.detail.address.cityName;
      cityNameElement.dispatchEvent(new Event('change'));

      const stateCodeElement = document.getElementById('address-stateCode');
      stateCodeElement.value = event.detail.address.stateCode;
      stateCodeElement.dispatchEvent(new Event('change'));

      const zipCodeElement = document.getElementById('address-zipCode');
      zipCodeElement.value = event.detail.address.zipCode;
      zipCodeElement.dispatchEvent(new Event('change'));

      this.addressChanged(event);
    },
    associationToggleBecameVisible(isVisible) {
      const associationToggle = document.querySelector('#toggleAssociation');
      if (isVisible) {
        associationToggle.addEventListener(changeEventName, this.associationToggleChanged);
        document.getElementById('toggleAssociation-modal-trigger').onclick = () => { this.track('association_help', 'interaction'); };
        this.fixApexToggleAlignmment('toggleAssociation');
      }
    },
    tradingSymbolCallBack(tradingLookupResp, symb) {
      const controlPersonInput = document.querySelector('#controlPersonInput');
      this.mapObject = tradingLookupResp;
      this.populateAssociationList('#controlPersonInput', this.mapObject);
      const searchKey = symb;
      controlPersonInput.dispatchEvent(
        new CustomEvent('listen-for-search-results', {
          bubbles: true,
          composed: true,
          detail: {
            searchResults: controlPersonInput.listOfItems,
            searchKey,
            isFuzzy: false,
          },
        }),
      );
    },
    cpBecameVisible(isVisible) {
      const controlPersonInput = document.querySelector('#controlPersonInput');
      if (isVisible) {
        controlPersonInput.validationText.autocomplete.required = this
          .pd_content.trading_or_cusip_validation_text.required;
        controlPersonInput.validationText.autocomplete.min = this
          .pd_content.trading_or_cusip_validation_text.min;
        controlPersonInput.validationText.autocomplete.regex = this
          .pd_content.trading_or_cusip_validation_text.regex;
        controlPersonInput.validationRules = this.buildRules('controlPerson');
        controlPersonInput.listOfItems = [];
        controlPersonInput.addEventListener(changeEventName, this.controlPersonValueChanged);
        controlPersonInput.addEventListener('dispatch-search-key', (evt) => {
          this.tradingSymbolLookup(evt.detail, this.tradingSymbolCallBack);
        });
      }
    },
    employmentStatusChanged(event) {
      const value = event.detail.selectValue;

      if (this.isManagedAccount) {
        let companyName = '';
        if (value === 'Z') {
          this.commitSecondaryUserData('', 'coAffillName');
          this.commitSecondaryUserData('', 'coAffillCode');
          companyName = this.pd_content.employment_details.fidelity_investments_label;
        }
        if (value === 'Z') {
          this.commitSecondaryUserData(companyName, 'employersName');
          document.querySelector('#companyName input').value = companyName;
        }
      }
      this.commitSecondaryUserData(value, 'employmentStatus');
    },
    employerDetailsChanged(event, fieldProperty) {
      this.commitSecondaryUserData(event.detail.value, fieldProperty);
    },

    companyNameVisible(isVisible) {
      const companyNameComponent = document.querySelector('#companyName');
      const companyNameInput = document.querySelector('#companyName input');
      const employerDetailsSection = ['E', 'S'];
      if (this.isManagedAccount) {
        employerDetailsSection.push('Z');
      }
      if (isVisible) {
        companyNameComponent.validationText.validations.required = this
          .pd_content.company_name_validation_text.required;
        companyNameComponent.validationText.validations.min = this
          .pd_content.company_name_validation_text.required;
        const validationRules = this.buildRules('companyName').employerName;
        companyNameComponent.validationRules.validations = validationRules;
        companyNameInput.minlength = validationRules.min;
        companyNameInput.maxlength = validationRules.max;
        companyNameInput.type = 'text';
      } else if (!employerDetailsSection.includes(this.su_employmentStatus)) {
        companyNameComponent.validationRules.validations = common.optionalCommonValue;
        companyNameInput.minlength = common.optionalCommonValue.min;
        companyNameInput.maxlength = common.optionalCommonValue.max;
      }
    },

    occupationVisible(isVisible) {
      const occupationComponent = document.querySelector('#employerOccupation');
      const occupationInput = document.querySelector('#employerOccupation input');
      const employerDetailsSection = ['E', 'S'];
      if (this.isManagedAccount) {
        employerDetailsSection.push('Z');
      }
      if (isVisible) {
        occupationComponent.validationText.validations.required = this
          .pd_content.occupation_validation_text.required;
        occupationComponent.validationText.validations.min = this
          .pd_content.occupation_validation_text.required;
        const validationRules = this.buildRules('occupation').occupation;
        occupationComponent.validationRules.validations = validationRules;
        occupationInput.minlength = validationRules.min;
        occupationInput.maxlength = validationRules.max;
        occupationInput.type = 'text';
      } else if (!employerDetailsSection.includes(this.su_employmentStatus)) {
        occupationComponent.validationRules.validations = common.optionalCommonValue;
        occupationInput.minlength = common.optionalCommonValue.min;
        occupationInput.maxlength = common.optionalCommonValue.max;
      }
    },

    controlPersonToggleChanged(toggleValue) {
      this.commitSecondaryUserData(toggleValue, 'coDirectorCode');
      const controlPersonInput = document.querySelector('#controlPersonInput');
      if (controlPersonInput && toggleValue === 'No') {
        controlPersonInput.removeEventListener(changeEventName, this.controlPersonValueChanged);
      }
      this.su_coDirectorCode = toggleValue;
    },
    controlPersonValueChanged(event) {
      this.commitSecondaryUserData(event.detail.value, 'coDirectorSymbol');
      const cusipId = this.populateIdFromMap(event.detail.value, this.mapObject);
      this.commitSecondaryUserData(cusipId, 'cusip');
    },
    associationToggleChanged(event) {
      const { detail } = event;
      this.commitSecondaryUserData(detail, 'coAffillCode');
      const companyNameInput = document.querySelector('#companyNameInput');
      if (companyNameInput && detail === 'No') {
        companyNameInput.removeEventListener(changeEventName, this.companyNameValueChanged);
      }
      if (event.detail === 'No') {
        this.track('association_toggle_No', 'interaction');
      } else {
        this.track('association_toggle_Yes', 'interaction');
      }
    },
    companyNameValueChanged(event) {
      this.commitSecondaryUserData(event.detail.value, 'coAffillName');
      const affId = this.populateIdFromMap(event.detail.value, this.pd_affiliations);
      this.commitSecondaryUserData(affId, 'coAffillId');
    },
    addressChanged(event) {
      this.commitSecondaryUserData(event.detail.address.addressLine1, 'addresses.employer.line1');
      this.commitSecondaryUserData(event.detail.address.addressLine2, 'addresses.employer.line2');
      this.commitSecondaryUserData(event.detail.address.cityName, 'addresses.employer.city');
      this.commitSecondaryUserData(event.detail.address.stateCode, 'addresses.employer.state');
      this.commitSecondaryUserData(event.detail.address.zipCode, 'addresses.employer.zip');
    },
    companyNameBecameVisible(isVisible) {
      if (isVisible) {
        const autoCompleteElement = document.querySelector('#companyNameInput');
        this.populateAssociationList('#companyNameInput', this.pd_affiliations);
        autoCompleteElement.validationText.autocomplete.min = this
          .pd_content.association_country_validation_text.min;
        autoCompleteElement.validationText.autocomplete.regex = this
          .pd_content.association_country_validation_text.regex;
        autoCompleteElement.validationText.autocomplete.required = this
          .pd_content.association_country_validation_text.required;
        autoCompleteElement.validationRules = this.buildRules('affiliation');
        autoCompleteElement.addEventListener(changeEventName, this.companyNameValueChanged);
        autoCompleteElement.addEventListener('dispatch-search-key', (evt) => {
          const searchKey = evt.detail;
          autoCompleteElement.dispatchEvent(
            new CustomEvent('listen-for-search-results', {
              bubbles: true,
              composed: true,
              detail: {
                searchResults: autoCompleteElement.listOfItems,
                searchKey,
                isFuzzy: false,
              },
            }),
          );
        });
      }
    },
  },
};
</script>
